/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from 'react';

import { jsx } from '@compiled/react';
import { IntlProvider } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags';

import { resolveInitialLocale } from './detect-locale';
import { DEFAULT_LOCALE, getDescriptor, type LocaleCode, type LocaleMessages } from './locales';
import { writePersistedLocale } from './storage';

type LocaleContextValue = {
	locale: LocaleCode;
	setLocale: (next: LocaleCode) => void;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

export type LocaleProviderProps = {
	children: ReactNode;
	/**
	 * Optional override, primarily for Storybook / VR examples that want to
	 * pin a specific locale. Production code should leave this unset and
	 * let the persisted/browser/fallback resolver decide.
	 */
	initialLocale?: LocaleCode;
};

/**
 * Owns the active locale for the microsite, lazily loads the matching
 * Traduki messages bundle, and renders <IntlProvider> for descendants.
 *
 * On first render: messages are not yet loaded, so we render with
 * `messages={undefined}` — react-intl-next then falls back to each
 * <FormattedMessage>'s `defaultMessage`, which is the English source
 * string. This avoids a flash of empty strings while the bundle loads.
 *
 * Gated behind the `twg-microsite-enable-i18n` feature flag. When the
 * gate is OFF, this provider skips all i18n machinery (no locale
 * detection, no persisted-locale storage, no lazy message bundle
 * loading, and no LocaleContext) and instead renders a bare
 * <IntlProvider locale={DEFAULT_LOCALE}> with no `messages`. Every
 * <FormattedMessage> inside the experience package will then fall back
 * to its `defaultMessage` (the English source string) and `useIntl()`
 * keeps working. Callers of `useLocale()` / `useSetLocale()` must live
 * behind the same gate (the LanguageSwitcher is the only such caller
 * today).
 */
export const LocaleProvider = ({ children, initialLocale }: LocaleProviderProps): JSX.Element => {
	if (!fg('twg-microsite-enable-i18n')) {
		return <IntlProvider locale={DEFAULT_LOCALE}>{children}</IntlProvider>;
	}

	return <LocaleProviderInner initialLocale={initialLocale}>{children}</LocaleProviderInner>;
};

const LocaleProviderInner = ({
	children,
	initialLocale,
}: LocaleProviderProps): JSX.Element => {
	const [locale, setLocaleState] = useState<LocaleCode>(
		() => initialLocale ?? resolveInitialLocale(),
	);
	const [messages, setMessages] = useState<LocaleMessages | undefined>(undefined);

	useEffect(() => {
		let cancelled = false;
		const descriptor = getDescriptor(locale);
		descriptor
			.loadMessages()
			.then((loaded) => {
				if (!cancelled) {
					setMessages(loaded);
				}
			})
			.catch((err) => {
				// Non-fatal: log to console; surfaces to Sentry via the
				// global error handler already wired by initSentry.
				// eslint-disable-next-line no-console
				console.error(`[LocaleProvider] Failed to load messages for "${locale}"`, err);
			});
		return () => {
			cancelled = true;
		};
	}, [locale]);

	const setLocale = useCallback((next: LocaleCode) => {
		writePersistedLocale(next);
		setLocaleState(next);
	}, []);

	const ctx = useMemo<LocaleContextValue>(() => ({ locale, setLocale }), [locale, setLocale]);

	return (
		<LocaleContext.Provider value={ctx}>
			<IntlProvider locale={locale} defaultLocale={DEFAULT_LOCALE} messages={messages}>
				{children}
			</IntlProvider>
		</LocaleContext.Provider>
	);
};

export const useLocale = (): LocaleCode => {
	const ctx = useContext(LocaleContext);
	if (!ctx) {
		throw new Error('useLocale must be used inside <LocaleProvider>');
	}
	return ctx.locale;
};

export const useSetLocale = (): ((next: LocaleCode) => void) => {
	const ctx = useContext(LocaleContext);
	if (!ctx) {
		throw new Error('useSetLocale must be used inside <LocaleProvider>');
	}
	return ctx.setLocale;
};

// Re-export catalog so consumers can build their own selectors if needed.
export {
	DEFAULT_LOCALE,
	SELECTABLE_LOCALES,
	SUPPORTED_LOCALES,
	type LocaleCode,
	type LocaleDescriptor,
} from './locales';
